/**
 * Learn — Importing Synchro UTDF timing into a time-distance diagram.
 * Framing V10 page 4. Target queries: "Synchro UTDF import",
 * "UTDF time-space diagram". Field mapping mirrors src/lib/parsers/utdf.ts.
 */

import Link from '../Link';
import LearnLayout from './LearnLayout';
import { TIER_LIMIT } from '../../lib/tier';
import type { PublicRoute } from '../../lib/route';

const UTDF_SAMPLE = `[Network]
RECORDNAME,DATA
Metric,1

[Nodes]
INTID,TYPE,X,Y,Z,DESCRIPTION
1,1,0,0,0,Oak St
2,1,0,350,0,Elm Ave
3,0,180,350,0,Driveway

[Timeplans]
RECORDNAME,INTID,DATA
Cycle Length,1,120
Offset,1,0
Cycle Length,2,120
Offset,2,21

[Phases]
RECORDNAME,INTID,D1,D2,D3,D4
MaxGreen,1,36,18,18,14
Yellow,1,3,3,3,3
AllRed,1,1,1,1,1`;

export default function SynchroUtdfImportPage({ route }: { route: PublicRoute }) {
  return (
    <LearnLayout
      route={route}
      heading="Importing Synchro UTDF timing into a time-distance diagram"
      lede="Synchro's Universal Traffic Data Format export carries the node coordinates, cycle lengths, offsets and phase timings that a time-distance (time-space) diagram needs. SigTDD reads those sections directly, so a corridor already coded in Synchro can be drawn and re-timed in the browser without retyping a number. This page shows the export step, the exact field mapping and what to check afterwards."
      ctaLabel="Open the tool and import a UTDF file"
    >
      <h2 id="export">1. Export from Synchro</h2>
      <ol>
        <li>Open the network in Synchro and select the arterial you want to coordinate.</li>
        <li>Choose <strong>File → Export → UTDF</strong> (in older versions <strong>Transfer → Data Access → Export</strong>).</li>
        <li>Export the <strong>combined</strong> UTDF file: a single CSV text file containing bracketed sections such as <code>[Network]</code>, <code>[Nodes]</code>, <code>[Links]</code>, <code>[Timeplans]</code> and <code>[Phases]</code>. If Synchro offers separate files per section, export the combined form; SigTDD detects the format by the presence of the <code>[Network]</code> and <code>[Nodes]</code> headers.</li>
        <li>Keep the file's <code>.csv</code> or <code>.txt</code> extension; both are accepted by the Import button.</li>
      </ol>

      <h2 id="import">2. Import into SigTDD</h2>
      <p>
        Press <strong>Import</strong> in the toolbar and pick the file. The format is detected from the
        content, the corridor is built and drawn, and any warnings, such as nodes skipped because they are
        unsignalised or coordinates converted from feet, are listed in the browser console. Synchro UTDF
        import is a Pro-tier capability (the free tier imports CSV and SigTDD JSON); the tier table on the{' '}
        <Link to="/pricing">pricing page</Link> is read from the same source the API enforces.
      </p>

      <h2 id="mapping">3. What is read, and how it becomes a corridor</h2>
      <p>A minimal file that exercises every section the importer uses:</p>
      <pre><code>{UTDF_SAMPLE}</code></pre>
      <table className="learn-table">
        <thead>
          <tr>
            <th>UTDF section and field</th>
            <th>Becomes</th>
            <th>Rule</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>[Network]</code> Metric</td>
            <td>Distance unit</td>
            <td><code>1</code> means coordinates are already in metres; anything else is treated as feet and multiplied by 0.3048.</td>
          </tr>
          <tr>
            <td><code>[Nodes]</code> INTID, TYPE</td>
            <td>Which nodes become intersections</td>
            <td>Only <code>TYPE = 1</code> (signalised) nodes are imported, in ascending INTID order. External, unsignalised and roundabout nodes are dropped and counted in a warning.</td>
          </tr>
          <tr>
            <td><code>[Nodes]</code> X, Y</td>
            <td><em>Dist N</em> — distance to the next intersection</td>
            <td>Straight-line distance between consecutive signalised nodes, rounded to the metre. The last intersection gets the default spacing because nothing follows it.</td>
          </tr>
          <tr>
            <td><code>[Nodes]</code> DESCRIPTION</td>
            <td>Intersection name and cross street</td>
            <td>Used as written; a missing description becomes <code>Int &lt;INTID&gt;</code>.</td>
          </tr>
          <tr>
            <td><code>[Timeplans]</code> Offset</td>
            <td><em>OS</em> — the offset</td>
            <td>Rounded to whole seconds; 0 when absent.</td>
          </tr>
          <tr>
            <td><code>[Timeplans]</code> Cycle Length</td>
            <td>Corridor cycle length</td>
            <td>Taken from the first intersection; if absent, the sum of its phase durations.</td>
          </tr>
          <tr>
            <td><code>[Phases]</code> MaxGreen, Yellow, AllRed</td>
            <td>Phase durations A–F</td>
            <td>Duration = green + yellow + all-red per phase column (D1, D2, …), rounded; zero-length phases are dropped; the first six phases are kept.</td>
          </tr>
          <tr>
            <td>(not in UTDF)</td>
            <td>Phase direction</td>
            <td>Assigned positionally: the first phase serves both directions, then alternately northbound and southbound. Review these; see step 4.</td>
          </tr>
          <tr>
            <td>(not in UTDF)</td>
            <td>Design speeds V N, V S</td>
            <td>Set to the tool default of 60 km/h. Enter the corridor's design speeds after import.</td>
          </tr>
        </tbody>
      </table>
      <p>
        The result is one corridor named after the file, with one timing plan called <em>UTDF Import</em>.
        Missing timing or phase data at an intersection falls back to defaults and is reported as a warning,
        never silently invented.
      </p>

      <h2 id="check">4. What to check after import</h2>
      <ol>
        <li>
          <strong>Node order.</strong> INTID order must follow the corridor from south to north (or west to
          east). If Synchro numbered the nodes in a different order, reorder the rows in the table or renumber
          the nodes before exporting.
        </li>
        <li>
          <strong>Distances.</strong> Node-to-node straight lines are shorter than the road on a curve, and
          Synchro node positions are not always at the stop line. Compare <em>Dist N</em> with measured
          stop-line spacing and correct it.
        </li>
        <li>
          <strong>Design speeds.</strong> Replace the 60 km/h default with the platoon speed for each
          direction; the trajectories and every offset calculation depend on it.
        </li>
        <li>
          <strong>Phase directions.</strong> Right-click each phase cell in the data grid and set whether it
          serves northbound, southbound, both or neither. UTDF describes phase timing, not which corridor
          movement a phase carries, so the positional guess needs a human check; a wrong direction shows up as
          a band passing through what should be red.
        </li>
        <li>
          <strong>Cycle length.</strong> Every intersection should show the same cycle; a double-cycled
          signal appears as a bar repeating at twice the rate and needs its own treatment.
        </li>
        <li>
          <strong>Phase count.</strong> Intersections with more than six timed phases are truncated; combine
          overlaps or represent the controller's ring structure with the six that matter for the corridor.
        </li>
      </ol>

      <h2 id="why">5. Why draw a Synchro corridor in a separate tool</h2>
      <p>
        Synchro is where the network is coded and the capacity analysis lives. The time-distance diagram is
        the coordination working drawing, and there are moments when it is useful on its own: showing a
        reviewer the green wave from a link without a licence, comparing three offset ideas side by side in
        seconds, or teaching the relationship between spacing, speed and cycle length. SigTDD is for those
        moments; it does not replace the capacity model, and the timings it produces go back into the
        controller database through the usual channels.
      </p>
      <p>
        Limits per corridor on the free and Pro tiers are {TIER_LIMIT.free.maxIntersectionsPerCorridor} and{' '}
        {TIER_LIMIT.pro.maxIntersectionsPerCorridor} intersections; a longer arterial is coordinated in sections,
        which is also how the field usually treats it. The{' '}
        <Link to="/learn/worked-example-corridor" event="learn-open" source="learn">worked example</Link>{' '}
        shows a six-signal section end to end, and the{' '}
        <Link to="/docs#utdf-import" event="docs-open" source="learn">documentation</Link> keeps the import
        reference beside the CSV and JSON formats.
      </p>
    </LearnLayout>
  );
}
